/**
 * The hub's chrome pieces — stage CTAs, the sticky bar's actions, the column
 * switcher — extracted from `DropHubSurface.tsx` against the 400-line
 * ceiling. Pure builders over the surface's derived data; no state.
 */
import React from "react";
import type {SurfaceContentBlockResponse} from "@/hooks/hub/useGetHub";
import {SlotFootprint, type SurfaceSlot} from "@/surfaces/slots";
import {HEADER_ALLOWANCE} from "@/surfaces/spine/useSlotScrollSpy";
import {sectionIcon, blockIcon} from "@/surfaces/spine/sectionIcons";
import type {SurfaceActionBarItem} from "@/surfaces/spine/SurfaceActionBar";
import type {OccupancyState} from "@/surfaces/occupancy";
import type {DropSection} from "@/hooks/drop/useGetDrop";

/*
 * SCROLLING TO A SLOT, for real.
 *
 * `scrollIntoView` is not enough here: the slot the board occupies can sit
 * inside a horizontally scrollable row, where the browser satisfies the call
 * by scrolling that row and leaving the page where it was — the bar lit up
 * and nothing moved. Scrolling the WINDOW to the element's own document
 * offset cannot be absorbed by an ancestor.
 *
 * The 92px allowance is the fixed header's height plus a breath — the same
 * number `scroll-margin-top` uses for the bands.
 */


/*
 * AFTER THE COMMIT, AND CONFIRMED.
 *
 * Selecting an entry sets state, and that re-render can re-run the occupancy
 * arbitration and remount the slot underneath a smooth scroll already in
 * flight — the browser abandons it and the page stays put, which is the
 * behaviour the bar showed. So: run on the frame after the commit, measure,
 * and if the element has not arrived, land it outright. The second pass is
 * what makes this reliable rather than usually-fine.
 */
/*
 * Only the LATEST request may correct the scroll. Each call used to arm its
 * own timer with nothing to cancel it, so a second selection within the
 * correction window was overridden by the first one's late snap-back.
 */
let scrollRequest = 0;

/**
 * Bring the stage into view. Swapping its occupant where it sits leaves a
 * member looking at the rail they tapped, with nothing to say the tap worked.
 */
export function scrollToStage(): void {
    const el = document.querySelector<HTMLElement>(".sh-hero");
    if (!el) return;
    requestAnimationFrame(() => {
        window.scrollTo({
            top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - HEADER_ALLOWANCE),
            behavior: "smooth",
        });
    });
}

export function scrollToSlot(slotId: string): void {
    const request = ++scrollRequest;

    const find = (): HTMLElement | null =>
        document.getElementById(`slot-${slotId}`) ??
            document.querySelector<HTMLElement>(`[data-slot-id="${slotId}"]`);

    const targetTop = (el: HTMLElement): number =>
        Math.max(0, el.getBoundingClientRect().top + window.scrollY - HEADER_ALLOWANCE);

    requestAnimationFrame(() => {
        if (request !== scrollRequest) return;
        const el = find();
        if (!el) return;
        window.scrollTo({top: targetTop(el), behavior: "smooth"});

        window.setTimeout(() => {
            if (request !== scrollRequest) return;
            const again = find();
            if (!again) return;
            const want = targetTop(again);
            /* Within a card's height is arrived; anything else was interrupted. */
            if (Math.abs(window.scrollY - want) > 80) {
                window.scrollTo({top: want, behavior: "auto"});
            }
        }, 450);
    });
}

export interface HubChromeDeps {
    railSections: DropSection[];
    sectionNavId: (s: DropSection, i: number) => string;
    isWheelSection: (s: DropSection) => boolean;
    wheelAccessible: boolean;
    requestWheel: () => void;
    setActiveSection: (id: string | null) => void;
}

/** The hero's section CTAs — the spin door (gated) and the board jump. */
export function buildSectionCtas(deps: HubChromeDeps & { spinIdx: number; boardIdx: number }): React.ReactNode[] {
    const {railSections, sectionNavId, isWheelSection, wheelAccessible, requestWheel, setActiveSection, spinIdx, boardIdx} = deps;
    return [
        ...spinIdx >= 0 && (!isWheelSection(railSections[spinIdx]) || wheelAccessible)
            ? [<button
                type="button"
                className="sh-cta"
                key="stage-cta-spin"
                /*
                 * SELECTING IS NOT ARRIVING — the same defect already fixed on
                 * the nav bar. These two CTAs only marked the entry active, so
                 * the hero's primary buttons lit something up and left the
                 * viewer exactly where they were: dead buttons.
                 */
                onClick={() => {
                    if (isWheelSection(railSections[spinIdx])) {
                        requestWheel();
                        return;
                    }
                    const navId = sectionNavId(railSections[spinIdx], spinIdx);
                    setActiveSection(navId);
                    scrollToSlot(navId);
                }}
            >
                {railSections[spinIdx].label}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
            </button>]
            : [],
        ...boardIdx >= 0
            ? [<button
                type="button"
                className="sh-cta sh-cta--g"
                key="stage-cta-board"
                onClick={() => {
                    const navId = sectionNavId(railSections[boardIdx], boardIdx);
                    setActiveSection(navId);
                    scrollToSlot(navId);
                }}
            >
                ◎ {railSections[boardIdx].label}
            </button>]
            : [],
    ];
}

/**
 * The sticky bar IS the page's navigation: every section, every TITLED band
 * (an authored title is the opt-in), then the purchase link. Wheel entries
 * obey the no-spins rule.
 */
export function buildBarActions(deps: HubChromeDeps & {
    liveBlockSlots: SurfaceSlot[];
    contentBlockById: Map<string, SurfaceContentBlockResponse>;
    purchaseUrl?: string;
    purchaseCta: string;
    activeSection: string | null;
}): SurfaceActionBarItem[] {
    const {railSections, sectionNavId, isWheelSection, requestWheel, setActiveSection, liveBlockSlots, contentBlockById, purchaseUrl, purchaseCta, activeSection} = deps;
    return [
        ...railSections
            .map((s, i) => ({s, i}))
            /*
             * The wheel is never a bar entry. It has a floating prompt of its
             * own and a modal, and its readiness is already said on the page —
             * a third place to say it is noise, not navigation.
             */
            .filter(({s}) => !isWheelSection(s))
            /*
             * A section can opt OUT of the bar, the way a block already can.
             * Some sections are a destination — the board, the streak — and
             * some are just content the page carries; a bar with an entry for
             * everything is a bar that helps with nothing.
             */
            .filter(({s}) => (s as { showInNav?: boolean }).showInNav !== false)
            .map(({s, i}) => ({
                id: sectionNavId(s, i),
                /*
                 * The NAV gets its own label when one is set, same as blocks: a
                 * bar entry has a few characters, a heading has a line. Cast
                 * until the regenerated client carries the field.
                 */
                label: (s as { navLabel?: string }).navLabel || s.label || "",
                icon: sectionIcon(s.icon, s.type, s.engagementType),
                active: activeSection === sectionNavId(s, i),
                /*
                 * SELECTING IS NOT ARRIVING. `setActiveSection` marks the entry
                 * but moved nothing on screen, so every section entry in the bar
                 * was inert — it lit up and the page stayed where it was. The
                 * band entries already scrolled to their slot; sections now do
                 * the same, against the slot that carries their id.
                 */
                onClick: () => {
                    if (isWheelSection(s)) {
                        requestWheel();
                        return;
                    }
                    const navId = sectionNavId(s, i);
                    setActiveSection(navId);
                    scrollToSlot(navId);
                },
            })),
        /*
         * EVERY placed block can carry a nav entry, not only BAND ones.
         *
         * This used to filter to `SlotFootprint.BAND` BEFORE consulting
         * `showInNav`, so a block in any other footprint could never appear
         * in the bar however the operator set the control.
         * The admin toggle existed and did nothing, which is worse than not
         * offering it. `showInNav` is now the only gate, with the legacy
         * titled-block rule as the fallback when it is unset.
         */
        ...liveBlockSlots
            .filter(s => s.footprint !== SlotFootprint.STAGE)
            .flatMap(s => {
                const block = contentBlockById.get(s.id);
                if (!block || block.showInNav === false) return [];
                /*
                 * A block's authored title lives under `title` OR `sectionTitle`
                 * depending on its config type — PRODUCTS uses `sectionTitle`,
                 * so such a block failed the titled-block test and dropped out
                 * of the bar despite showing a title. Admin's
                 * own `findTitleEntry` already checks both; this now matches it.
                 */
                const cfg = Object.values(block).find((v): v is { title?: string; sectionTitle?: string } => {
                    if (!v || typeof v !== "object") return false;
                    const o = v as { title?: unknown; sectionTitle?: unknown };
                    return typeof o.title === "string" || typeof o.sectionTitle === "string";
                });
                const authored = cfg?.title ?? cfg?.sectionTitle;
                const title = authored ??
                    (block.type ? block.type.replace(/_/g, " ").toLowerCase() : undefined);
                /*
                 * The NAV gets its own label when one is set. A bar entry has a
                 * few characters to work with and a section heading has a line —
                 * a short word in the bar, the full heading on the section.
                 */
                const navLabel = block.navLabel || title;
                /*
                 * `showInNav` is the explicit control; unset falls back to the
                 * legacy rule where an authored title is the opt-in.
                 */
                if (!title || block.showInNav === undefined && !authored) return [];
                /*
                 * A band entry both SELECTS and scrolls. Selecting is what makes
                 * the bar show where you are; without it a block entry could
                 * never take the active treatment, so the bar's own state was
                 * only ever right for sections.
                 */
                const bandId = `band-${s.id}`;
                return [{
                    id: bandId,
                    label: navLabel,
                    icon: blockIcon(block.type, block.navIcon),
                    active: activeSection === bandId,
                    onClick: () => {
                        setActiveSection(bandId);
                        scrollToSlot(s.id);
                    },
                }];
            }),
        ...purchaseUrl
            ? [{id: "get", label: purchaseCta, href: purchaseUrl}]
            : [],
    ];
}

/** The contested-column switcher — labels resolved section-first, block-type fallback. */
export const DropHubColumnControl: React.FC<{
    occupancy: OccupancyState;
    sectionById: Map<string, { label?: string; title?: string }>;
    contentBlockById: Map<string, SurfaceContentBlockResponse>;
    onPin: (id: string) => void;
}> = ({occupancy, sectionById, contentBlockById, onPin}) =>
    <div className="sh-colctl" role="tablist" aria-label="Column content">
        {occupancy.candidateIds.map(id => {
            /* A slot id is never a label — an unlabeled content block names itself by type. */
            const label = sectionById.get(id)?.label ??
                sectionById.get(id)?.title ??
                contentBlockById.get(id)?.type?.replace(/_/g, " ").toLowerCase() ??
                id.replace(/[-_]/g, " ");
            return (
                <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={occupancy.columnOccupantId === id}
                    className={`sh-colctl__tab ${occupancy.columnOccupantId === id ? "sh-colctl__tab--active" : ""}`}
                    onClick={() => onPin(id)}
                >
                    {label}
                </button>
            );
        })}
    </div>;
